// src/modules/app-views/offers/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import OffersInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import offersInitActionButton from 'modules/app-views/offers/pages/Init/actions/actionButton';
import offersInitMenuButton from 'modules/app-views/offers/pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import OffersDocument from 'modules/app-views/offers/pages/Document';
import offersDocumentActionButton from 'modules/app-views/offers/pages/Document/actions/actionButton';
import offersDocumentMenuButton from 'modules/app-views/offers/pages/Document/actions/menuButton';
import OffersParametersModal from 'modules/app-views/offers/pages/Init/modals/OffersParametersModal';
import seedOffersParameters from 'modules/app-views/offers/seed';
import offersDocumentDecorateRequest from './pages/Document/decorators/request';
import OffersDocumentCardHeaderColumn1 from 'modules/app-views/offers/pages/Document/components/OffersDocumentCardHeaderColumn1';
import OffersDocumentCardHeaderColumn2 from 'modules/app-views/offers/pages/Document/components/OffersDocumentCardHeaderColumn2';
import OffersDocumentCardHeaderColumn3 from 'modules/app-views/offers/pages/Document/components/OffersDocumentCardHeaderColumn3';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'offers';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: OffersParametersModal,
  parametersSeed: seedOffersParameters,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: null,
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <OffersInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.OFFERS.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      responseDoc: null,
      responseData: null,
    },



    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['documentCode', 'status', 'validity', 'type', 'price', 'createdAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', offersInitActionButton, offersInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <OffersDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.OFFERS.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: offersDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: { status: 'draft' },
      actions: ['editRefresh', offersDocumentActionButton(), offersDocumentMenuButton(), 'return'],
      viewMode: 'auto',
      backPage: 'init',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
        placeholder: 'offer',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} name={props?.name} />,
        subtitle: props => <CardHeaderSubtitle {...props} fieldName="shortDescription" />,

        column1: props => <OffersDocumentCardHeaderColumn1 {...props} />,
        column2: props => <OffersDocumentCardHeaderColumn2 {...props} />,
        column3: props => <OffersDocumentCardHeaderColumn3 {...props} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {},
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
